<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { uuid as newUuid } from '@addictivegroup/addictive-offline'
import { api } from '@/lib/http.js'
import { useMeta } from '@/stores/meta.js'
import { useUi } from '@/stores/ui.js'
import { offline } from '@/lib/offline.js'
import { formatAddress, formatDateTime } from '@/lib/format.js'
import { displayTaxId, registryOf, taxIdProblem } from '@/lib/taxId.js'
import { addressRoles, sameAddress, toggleRole } from '@/lib/addressRoles.js'

/**
 * A company's tax number (D-199), checked as its registration country checks it, and what a
 * public registry says of the company: a Polish one from the white list, another EU one from
 * VIES. Typed in, it is looked up; "Fill in" takes the name (as ordinary writing), REGON, KRS and
 * the registered office's address into the form — what someone typed is replaced only when they
 * say so, and an account with this number already is shown instead.
 */
const props = defineProps({
  entity: { type: String, required: true },
  inputs: { type: Object, required: true },
  record: { type: Object, default: null },
  id: { type: String, default: '' },
  disabled: Boolean,
  invalid: Boolean,
})
const emit = defineEmits(['set'])
const meta = useMeta()
const ui = useUi()
const { t } = useI18n()

const country = computed(() => (props.inputs.registration_country || meta.data?.home_country || 'PL').toUpperCase())
const value = computed(() => props.inputs.tax_id || '')
const problem = computed(() => taxIdProblem(country.value, value.value))
const registry = computed(() => registryOf(country.value))
const found = ref(null)
const busy = ref(false)
const failure = ref('')
const nameField = computed(() => (props.entity === 'lead' ? 'company_name' : 'name'))
// Accounts with this number, other than this one.
const existing = computed(() => (found.value?.existing || []).filter((a) => a.uuid !== props.record?.uuid))

let timer = null
let typed = false
function input(v) {
  typed = true
  emit('set', 'tax_id', v || null)
}
// Looked up once typed and right (not on opening a record that has one).
watch(() => [value.value, country.value], () => {
  found.value = null
  failure.value = ''
  clearTimeout(timer)
  if (!typed || !value.value || problem.value || !registry.value || !offline.state.online) return
  timer = setTimeout(lookup, 600)
})

async function lookup() {
  busy.value = true
  failure.value = ''
  try {
    found.value = (await api('/registry/lookup', { query: { country: country.value, tax_id: value.value }, timeoutMs: 20000 })).data
  } catch (e) {
    found.value = null
    failure.value = e?.data?.message || e?.message || t('registry.unavailable')
  } finally {
    busy.value = false
  }
}

const vatText = (status) => (status === 'valid' ? t('registry.vatValid') : status === 'invalid' ? t('registry.vatInvalid') : status || '')

/** The registry's data into the form; what was typed differently is replaced only when the person says so. */
async function fill() {
  const f = found.value
  if (!f) return
  const replace = async (current, next, what) => !current || current === next || (await ui.ask({ title: t('registry.replaceTitle'), message: t('registry.replace', { what, current, next }), confirmLabel: t('registry.replaceYes') }))
  if (f.name && (await replace(props.inputs[nameField.value], f.name, t('registry.name')))) emit('set', nameField.value, f.name)
  if (props.entity === 'account') {
    if (f.regon && (await replace(props.inputs.regon, f.regon, 'REGON'))) emit('set', 'regon', f.regon)
    if (f.krs && (await replace(props.inputs.krs, f.krs, 'KRS'))) emit('set', 'krs', f.krs)
    emit('set', 'registry_lookup', f.uuid)
    if (f.address) {
      const roles = addressRoles(meta)
      const list = [...(props.inputs.addresses || [])]
      let at = list.findIndex((a) => sameAddress(a, f.address))
      if (at < 0) {
        list.push({ uuid: newUuid(), roles: [], label: null, ...f.address, linked_address_uuid: null })
        at = list.length - 1
      }
      // The registered office: here; the role leaves any other address.
      emit('set', 'addresses', (list[at].roles || []).includes('registered') ? list : toggleRole(list, at, 'registered', roles))
    }
  } else if (f.address && (await replace(formatAddress(props.inputs.address), formatAddress(f.address), t('registry.address')))) {
    emit('set', 'address', f.address)
  }
  ui.success(t('registry.filled'))
}

const checked = computed(() => props.record?.registry || null)
</script>

<template>
  <div>
    <div class="flex items-center gap-2">
      <input :id="id || undefined" :class="['input min-w-0 flex-1', invalid || problem ? 'invalid' : '']" :value="displayTaxId(country, value)" :disabled="disabled" :placeholder="country === 'PL' ? '000-000-00-00' : ''" data-testid="tax-id" @input="input($event.target.value)" />
      <button v-if="registry && value && !problem && !disabled" type="button" class="shrink-0 rounded-lg border border-default px-2.5 py-1.5 text-sm hover:bg-[var(--surface-3)]" :disabled="busy || !offline.state.online" :title="$t(registry === 'mf' ? 'registry.askMf' : 'registry.askVies')" data-testid="registry-lookup" @click="lookup">
        <i :class="['fa-solid', busy ? 'fa-spinner fa-spin' : 'fa-magnifying-glass']" aria-hidden="true"></i>
      </button>
    </div>
    <p v-if="problem" class="mt-1 text-xs text-red-600" data-testid="tax-id-problem">{{ $t(problem.key, problem.values || {}) }}</p>
    <p v-else-if="checked && !found" class="mt-1 text-xs text-faint" data-testid="registry-checked">
      {{ $t(checked.source === 'mf' ? 'registry.checkedMf' : 'registry.checkedVies', { date: formatDateTime(checked.checked_at) }) }}<span v-if="checked.vat_status"> · {{ $t('registry.vat') }}: {{ vatText(checked.vat_status) }}</span>
    </p>
    <p v-if="failure" class="mt-1 text-xs text-amber-700 dark:text-amber-300" data-testid="registry-failure">{{ failure }}</p>
    <div v-if="found" class="mt-2 rounded-lg border border-default bg-[var(--surface-2)] p-3 text-sm" data-testid="registry-card">
      <template v-if="!found.found">
        <p class="text-muted">{{ $t(found.source === 'mf' ? 'registry.notFoundMf' : 'registry.notFoundVies') }}</p>
      </template>
      <template v-else>
        <p class="text-xs text-faint">{{ $t(found.source === 'mf' ? 'registry.fromMf' : 'registry.fromVies') }}</p>
        <p v-if="found.name" class="font-medium" data-testid="registry-name">{{ found.name }}</p>
        <p v-if="found.source === 'vies' && !found.disclosed" class="text-muted">{{ $t('registry.undisclosed', { country }) }}</p>
        <p class="text-muted">
          <span>{{ $t('registry.vat') }}: {{ vatText(found.vat_status) }}</span>
          <span v-if="found.regon"> · REGON {{ found.regon }}</span>
          <span v-if="found.krs"> · KRS {{ found.krs }}</span>
        </p>
        <p v-if="found.address" class="text-muted">{{ formatAddress(found.address) }}<span v-if="found.address.region">, {{ found.address.region }}</span></p>
        <p v-if="existing.length" class="mt-2 text-amber-700 dark:text-amber-300" data-testid="registry-existing">
          <i class="fa-solid fa-circle-info mr-1" aria-hidden="true"></i>{{ $t('registry.exists') }}
          <router-link v-for="a in existing" :key="a.uuid" class="link ml-1" :to="{ name: 'record', params: { entityPath: 'accounts', uuid: a.uuid } }">{{ a.label }} <i class="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i></router-link>
        </p>
        <button v-else-if="!disabled && (found.name || found.address)" type="button" class="mt-2 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700" data-testid="registry-fill" @click="fill">
          <i class="fa-solid fa-wand-magic-sparkles mr-1" aria-hidden="true"></i>{{ $t('registry.fill') }}
        </button>
      </template>
    </div>
  </div>
</template>
